import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Label,
  Chip,
  Icon,
  Menu,
  MenuItem,
  Tooltip,
  Utils,
  DateUtils,
} from 'mw-style-react';
import { DATE_FORMAT_3 } from 'constants';
import AppUtils from '@control-front-end/utils/utils';
import cn from 'classnames';
import useIntl from 'useIntl';
import CorrectiveTransaction from './components/CorrectiveTransaction';
import TrsDetails from './components/TrsDetails';
import mes from './intl';
import './TransactionsList.scss';

function TransactionsList(props) {
  const {
    debitId,
    creditId,
    transactions,
    currencyName,
    showTotal,
    readOnly,
    handleRemove,
    handleCorrect,
  } = props;
  const t = useIntl();
  const [menu, toggleMenu] = useState(null);
  const [popup, togglePopup] = useState(null);
  const [correctiveItem, setCorrective] = useState(null);
  const transactionsSettings =
    useSelector((state) => state.config.transactions) || {};
  const disableRemove = transactionsSettings.disableRemove || false;

  /**
   * Обработка клика в меню транзакции
   */
  const handleClickMenu = ({ value, transaction }) => {
    switch (value) {
      case 'remove':
        handleRemove(menu);
        break;
      case 'details':
        togglePopup(transaction);
        break;
      case 'correct':
        setCorrective(menu);
        break;
      default:
    }
    toggleMenu(null);
  };

  /**
   * Отрисовать меню для транзакции
   */
  const renderMenu = (transaction) => {
    return (
      <div styleName="table__menu" onClick={(e) => e.stopPropagation()}>
        <Menu
          onClick={({ value }) => handleClickMenu({ value, transaction })}
          onClose={() => toggleMenu(null)}
        >
          <MenuItem
            label={t(mes.createCorrective)}
            value="correct"
            visibility={readOnly ? 'disabled' : 'visible'}
          />
          <MenuItem label={t(mes.transactionDetails)} value="details" />
          <MenuItem
            label={t(mes.remove)}
            value="remove"
            visibility={disableRemove ? 'disabled' : 'visible'}
          />
          <Tooltip
            styleName="tooltip"
            value={disableRemove ? t(mes.removeInfo) : ''}
          >
            <div
              styleName={cn('table__menu__info', { hidden: !disableRemove })}
            />
          </Tooltip>
        </Menu>
      </div>
    );
  };

  const getStatusIcon = (type, children) => {
    switch (type) {
      case 'authorized':
        return children ? 'flag' : 'clock';
      case 'completed':
        return 'check';
      case 'declined':
      case 'reversed':
      case 'canceled':
        return 'close';
      default:
        return null;
    }
  };

  // Шапка таблицы транзакций
  const renderTableHead = () => (
    <TableHead fixed={true}>
      <TableRow styleName="table__row" rowSize="small">
        <TableCell styleName="table__cell">{t(mes.date)}</TableCell>
        <TableCell styleName="table__cell">{t(mes.actorName)}</TableCell>
        <TableCell styleName="table__cell status">{t(mes.status)}</TableCell>
        <TableCell styleName="table__cell">{t(mes.comment)}</TableCell>
        <TableCell styleName="table__cell type">
          {t(mes.transactionType)}
        </TableCell>
        <TableCell styleName="table__cell amount">
          {t(mes.transactionAmount)}
        </TableCell>
        <TableCell
          styleName="table__cell amount"
          visibility={showTotal ? 'visible' : 'hidden'}
        >
          {t(mes.transactionTotal)}
        </TableCell>
        <TableCell styleName="table__menu__wrap" />
      </TableRow>
    </TableHead>
  );

  const renderTransactionRow = (
    transaction,
    handleClick = true,
    handleDetails = true
  ) => {
    const {
      id,
      type,
      amount,
      total,
      createdAt,
      expiration,
      title,
      incomeType,
      comment,
      children,
    } = transaction;
    return (
      <TableRow
        key={id}
        id={id}
        styleName={cn('table__row', { clickable: handleClick })}
        onClick={handleClick ? () => togglePopup(transaction) : () => {}}
      >
        <TableCell styleName="table__cell">
          {DateUtils.toDate(createdAt / 1000, DATE_FORMAT_3)}
          {expiration ? (
            <div title={t(mes.expiration)} styleName="expiration">
              <span>{t(mes.exp)}</span>:&nbsp;
              {DateUtils.toDate(expiration, DATE_FORMAT_3)}
            </div>
          ) : null}
        </TableCell>
        <TableCell styleName="table__cell">{title}</TableCell>
        <TableCell styleName="table__cell status">
          <Icon
            styleName={cn('icon', type, { open: !children })}
            size="small"
            type={getStatusIcon(type, children)}
          />
          <Label styleName="status" value={Utils.toUpperLatter(type)} />
        </TableCell>
        <TableCell styleName="table__cell">{comment || '---'}</TableCell>
        <TableCell styleName="table__cell type">
          <Chip
            styleName={cn('chip', incomeType)}
            label={Utils.capitalize(incomeType)}
            size="small"
            closeIcon={false}
            fontWeight="normal"
            type="rectangular"
          />
        </TableCell>
        <TableCell styleName="table__cell amount">
          {AppUtils.toNumberFormat(amount)} {currencyName}
        </TableCell>
        <TableCell
          styleName="table__cell amount"
          visibility={showTotal ? 'visible' : 'hidden'}
        >
          {AppUtils.toNumberFormat(total)} {currencyName}
        </TableCell>
        <TableCell styleName="table__menu__wrap">
          {handleDetails ? (
            <Icon
              type="more"
              onClick={(e) => {
                e.stopPropagation();
                if (readOnly) return;
                toggleMenu(id);
              }}
              visibility={readOnly ? 'disabled' : 'visible'}
            />
          ) : (
            <i />
          )}
          {menu === id ? renderMenu(transaction) : null}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Table styleName="table" paddingSize="small">
        {renderTableHead()}
        <TableBody>
          {transactions.map((item) => (
            <Fragment key={item.id}>
              {renderTransactionRow(item)}
              {correctiveItem === item.id ? (
                <CorrectiveTransaction
                  accountId={item.incomeType === 'debit' ? debitId : creditId}
                  parentId={item.id}
                  total={item.total}
                  incomeType={item.incomeType}
                  currencyName={currencyName}
                  onClose={() => setCorrective(null)}
                  onSubmit={handleCorrect}
                />
              ) : null}
            </Fragment>
          ))}
        </TableBody>
      </Table>
      {popup ? (
        <Table styleName="table" paddingSize="small">
          <TableBody>
            <TrsDetails
              key={popup.id}
              transaction={popup}
              currencyName={currencyName}
              getStatusIcon={getStatusIcon}
              tableHead={renderTableHead}
              renderTransactionRow={renderTransactionRow}
              is2Step={popup.type === 'authorized'}
              onClose={(e) => {
                e.stopPropagation();
                togglePopup(null);
              }}
            />
          </TableBody>
        </Table>
      ) : null}
    </>
  );
}

TransactionsList.propTypes = {
  debitId: PropTypes.string,
  creditId: PropTypes.string,
  transactions: PropTypes.array,
  currencyName: PropTypes.string,
  showTotal: PropTypes.bool,
  readOnly: PropTypes.bool,
  handleRemove: PropTypes.func,
  handleCorrect: PropTypes.func,
};

export default TransactionsList;
